export const PAYMENT_RESULT_EVENT = "rocketfuel_result_ok";

/** Safety net: never hold the result forever if merchant loyalty config never loads. */
const UNKNOWN_LOYALTY_HOLD_TIMEOUT = 15000;

let heldPaymentResult = null;
let hasPostedPaymentResult = false;
let unknownLoyaltyTimeoutId = null;

function clearUnknownLoyaltyTimeout() {
  if (unknownLoyaltyTimeoutId) {
    clearTimeout(unknownLoyaltyTimeoutId);
    unknownLoyaltyTimeoutId = null;
  }
}

function postToParentWindows(data) {
  if (window.parent) {
    window.parent.postMessage(data, "*");
  }
  // Avoid duplicate delivery when parent and top are the same window
  if (window.top && window.top !== window.parent) {
    window.top.postMessage(data, "*");
  }
}

function mergePaymentResult(existing, incoming) {
  if (!existing) {
    return incoming;
  }
  if (!incoming) {
    return existing;
  }
  return {
    ...existing,
    ...incoming,
    response: {
      ...(existing.response || {}),
      ...(incoming.response || {}),
    },
    paymentCompleted:
      incoming.paymentCompleted ?? existing.paymentCompleted,
  };
}

/**
 * Integrations (WooCommerce plugin, JS SDK, hosted stores) treat the payment
 * result event as "flow finished" and close the iframe as soon as they get it.
 * With loyalty enabled the shopper still has the rewards screen to go through,
 * so the event is held back until that screen is resolved.
 *
 * Also hold while merchant loyalty config is unknown, so we do not post early
 * and close the iframe before the thank-you / rewards screen can load.
 */
export function shouldHoldPaymentResult(marketInfo) {
  if (!marketInfo || typeof marketInfo.enabledLoyalty !== "boolean") {
    return true;
  }
  return marketInfo.enabledLoyalty === true;
}

export function sendPaymentResult(data, marketInfo) {
  if (shouldHoldPaymentResult(marketInfo)) {
    heldPaymentResult = mergePaymentResult(heldPaymentResult, data);

    // Loyalty flag unknown: release on timeout so the integration is not stuck
    const loyaltyUnknown =
      !marketInfo || typeof marketInfo.enabledLoyalty !== "boolean";
    if (loyaltyUnknown && !unknownLoyaltyTimeoutId && !hasPostedPaymentResult) {
      unknownLoyaltyTimeoutId = setTimeout(() => {
        unknownLoyaltyTimeoutId = null;
        releasePaymentResult();
      }, UNKNOWN_LOYALTY_HOLD_TIMEOUT);
    } else if (!loyaltyUnknown) {
      clearUnknownLoyaltyTimeout();
    }
    return false;
  }

  clearUnknownLoyaltyTimeout();

  const payload = mergePaymentResult(heldPaymentResult, data);
  heldPaymentResult = null;

  // Payment method + thank-you/result both notify; only post once
  if (hasPostedPaymentResult) {
    return false;
  }

  hasPostedPaymentResult = true;
  postToParentWindows(payload);
  return true;
}

export function releasePaymentResult() {
  clearUnknownLoyaltyTimeout();

  if (!heldPaymentResult) {
    return false;
  }

  const data = heldPaymentResult;
  heldPaymentResult = null;

  if (hasPostedPaymentResult) {
    return false;
  }

  hasPostedPaymentResult = true;
  postToParentWindows(data);
  return true;
}

export function hasHeldPaymentResult() {
  return heldPaymentResult !== null;
}

export function resetPaymentResultEvents() {
  clearUnknownLoyaltyTimeout();
  heldPaymentResult = null;
  hasPostedPaymentResult = false;
}
